import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { ads } from "~src/api";
import { TrendingAd } from "~src/types/ad";
import * as Sentry from "@sentry/react";
import { createAbortController } from "~src/api/common/api";

export type TrendingAdsContext = {
  data: TrendingAd[];
  loadMore: () => void;
  loading: boolean;
  total: number | null;
  count: number | null;
  full_count: number | null;
  validCount: number;  // Count of ads after client-side filtering
  setValidCount: (count: number) => void;
  error?: boolean;
  errorMessage?: string;
  initialLoad: boolean;
};

export const useInfiniteTrendingAds = (
  params: ads.TrendingAdsParams,
): TrendingAdsContext => {
  const [data, setData] = useState<TrendingAd[]>([]);
  const [total, setTotal] = useState<null | number>(null);
  const [count, setCount] = useState<null | number>(null);
  const [fullCount, setFullCount] = useState<null | number>(null);
  const [validCount, setValidCount] = useState<number>(0);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [hasMore, setHasMore] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<boolean>(false);
  const [errorMessage, setErrorMessage] = useState<string | undefined>(undefined);
  const [initialLoad, setInitialLoad] = useState<boolean>(false);

  const filterUniqueAds = (ads: TrendingAd[], existingAds: TrendingAd[]) => {
    const existingAdIds = new Set(existingAds.map((ad) => ad.id));
    return ads.filter((ad) => !existingAdIds.has(ad.id));
  };
  
  const loadMore = useCallback(async () => {
    if (hasMore && !loading && !initialLoad) {
      const { signal, cleanup } = createAbortController();
      setLoading(true);
      
      try {
        const response = await ads.getTrendingAds({
          ...params,
          page: currentPage + 1,
        }, { signal });
        
        if (!signal.aborted) {
          if (response.results.length > 0) {
            setData((prevItems) => [...prevItems, ...response.results]);
            setCurrentPage((prevPage) => prevPage + 1);
            setHasMore(!!response.next);
          } else {
            setHasMore(false);
          }
        }
      } catch (err) {
        if (!signal.aborted) {
          // Check if it's a timeout error
          const isTimeout = axios.isAxiosError(err) && err.code === 'ECONNABORTED';
          if (isTimeout) {
            setError(true);
            setErrorMessage('Request timed out. Please try again or adjust your filters.');
          } else {
            Sentry.withScope((scope) => {
              scope.setTag("module", "useInfiniteTrendingAds");
              scope.setExtra("operation", "loadMore");
              Sentry.captureException(err);
            });
            setError(true);
            setErrorMessage(undefined);
          }
        }
      } finally {
        if (!signal.aborted) {
          setLoading(false);
        }
      }

      // Since this is not in a useEffect, we should manually call the cleanup
      // if we detect a navigation or other fast interaction
      setTimeout(() => {
        if (signal && !signal.aborted) {
          cleanup();
        }
      }, 5000); // 5 seconds timeout
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, hasMore, loading, initialLoad]);

  // Fetch initial data
  useEffect(() => {
    const { cleanup, signal } = createAbortController();
    
    const fetchData = async () => {
      setData([]);
      setTotal(null);
      setCount(null);
      setFullCount(null);
      setInitialLoad(true);
      setCurrentPage(1);
      setLoading(true);
      try {
        const response = await ads.getTrendingAds({
          ...params,
          page: 1, // explicitly start with page 1
        }, { signal });
        
        // Don't update state if component unmounted
        if (!signal.aborted) {
          const uniqueAds = filterUniqueAds(response.results, []);
          setData(uniqueAds);
          setHasMore(!!response.next);
          setTotal(response.full_count);
          setCount(response.count);
          setFullCount(response.full_count);
        }
      } catch (err) {
        // Don't report canceled requests to Sentry
        if (!signal.aborted) {
          // Check if it's a timeout error
          const isTimeout = axios.isAxiosError(err) && err.code === 'ECONNABORTED';
          if (isTimeout) {
            setError(true);
            setErrorMessage('Request timed out. Please try again or adjust your filters.');
          } else {
            Sentry.withScope((scope) => {
              scope.setTag("module", "useInfiniteTrendingAds");
              scope.setExtra("operation", "initialFetch");
              Sentry.captureException(err);
            });
            setError(true);
            setErrorMessage(undefined);
          }
        }
      } finally {
        if (!signal.aborted) {
          setLoading(false);
          setInitialLoad(false);
        }
      }
    };
    
    fetchData();
    
    // Cleanup function to cancel request on unmount or dependency change
    return cleanup;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    params.search_text,
    params.page_size,
    params.all_ads,
    params.ad_country,
    params.ad_status,
    params.date_range,
    params.media_type,
    params.cta,
    params.ordering,
    params.platform,
    params.duration,
    params.page_likes,
  ]);

  return {
    data,
    loadMore,
    loading,
    error,
    errorMessage,
    total,
    count,
    full_count: fullCount,
    validCount,
    setValidCount,
    initialLoad
  };
};
